import React, { useContext, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DECADES } from "../constants.js";
import { ArchiveContext } from "../App.js";
import SEOMeta from "../components/SEOMeta.js";

const DecadesPage: React.FC = () => {
  const { eraCounts, isLoading, getEraCounts } = useContext(ArchiveContext);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search")?.toLowerCase();

  useEffect(() => {
    getEraCounts();
  }, [getEraCounts]);

  const canonicalUrl = `https://cyberannals.com/decades`;

  if (isLoading && Object.keys(eraCounts).length === 0) {
    return (
      <>
        <SEOMeta
          title="Cyber Attack  | Historical Timeline 1940-2025"
          description="Complete decade-by-decade analysis of cyber security history. Explore cyber attacks timeline from WWII cryptography to modern ransomware."
          keywords="cyber, virus, cyber attacks, cyber attack, recent cyber attacks, cyber attack news, cyberattack, cyber breach, cyber crime, cybercrime, cyber crimes, internet security, cyber awareness, stuxnet, cyber attacks by decade, cybersecurity timeline 1940-2025, historical cyber attacks, decade analysis, cybersecurity evolution timeline, cold war cyber espionage, internet era hacking, modern ransomware history"
          canonical={canonicalUrl}
          // ogImage="https://cyberannals.com/images/decades-timeline.png"
          structuredData={{
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: DECADES.map((decade, index) => ({
              "@type": "ListItem",
              position: index + 1,
              item: {
                "@type": "WebPage",
                name: decade,
                description: `Cyber security events from ${decade}`,
                url: `https://cyberannals.com/decade/${encodeURIComponent(
                  decade
                )}`,
              },
            })),
          }}
        />
        <div className="min-h-[60vh] flex flex-col items-center justify-center space-y-6">
          <div className="w-10 h-10 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-500">
            Scanning Temporal Layers...
          </p>
        </div>
      </>
    );
  }

  // If there's a search query, we could filter here or redirect. For now, we list eras.
  return (
    <div className="space-y-12 sm:space-y-16 py-6 sm:py-12 animate-in fade-in duration-700 max-w-7xl mx-auto">
      <div className="text-center space-y-4 px-4">
        <nav className="flex items-center gap-2 text-[10px] font-black font-mono uppercase tracking-widest text-slate-500 mb-6">
          <Link to="/" className="hover:text-white transition-colors">
            Root
          </Link>
          <i className="fa-solid fa-slash text-[8px] opacity-20"></i>
          <span className="text-blue-500">Eras</span>
        </nav>
        <h1 className="text-4xl sm:text-7xl font-black tracking-tighter uppercase italic text-white leading-none">
          History Eras
        </h1>
        <p className="text-slate-500 text-[11px] font-black uppercase tracking-[0.4em] max-w-2xl mx-auto">
          Select an era and explore the{" "}
          <span className="text-blue-600">security breaches</span> happened in
          that era.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 px-4">
        {DECADES.map((decade, index) => (
          <Link
            key={decade}
            to={`/decade/${encodeURIComponent(decade)}`}
            className="group relative overflow-hidden p-10 rounded-[3.5rem] bg-white/5 border border-white/5 hover:border-blue-500 transition-all duration-500 hover:-translate-y-2 shadow-2xl"
          >
            <div className="flex justify-between items-start mb-12">
              <span className="text-6xl font-black opacity-[0.05] group-hover:text-blue-600 group-hover:opacity-10 transition duration-700 italic">
                #{String(index + 1).padStart(2, "0")}
              </span>
              <div className="flex flex-col items-end gap-1">
                <span className="px-3 py-1 bg-blue-600/10 text-blue-500 text-[10px] font-black uppercase tracking-widest rounded-xl border border-blue-500/20">
                  {eraCounts[decade] || 0} RECORDS
                </span>
              </div>
            </div>
            <h3 className="text-3xl font-black mb-4 group-hover:text-blue-500 transition uppercase tracking-tighter italic text-white">
              {decade}
            </h3>
            <p className="text-[14px] text-slate-400 leading-relaxed mb-10 font-bold italic opacity-80">
              Analyze the tactical shifts in the{" "}
              <span className="text-blue-500/80">cyber security history</span>{" "}
              of the {decade} era.
            </p>
            <div className="flex items-center text-blue-400 font-black text-[10px] uppercase tracking-[0.2em] gap-2">
              Analyze Ledger{" "}
              <i className="fa-solid fa-arrow-right-long group-hover:translate-x-2 transition-transform"></i>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default DecadesPage;
